import React, { useState, useEffect, useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import styled, { createGlobalStyle } from 'styled-components';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode.react';
import toast from 'react-hot-toast';
import ReactTooltip from 'react-tooltip';
import Card from './Card';
import Input, { TextAreaInput } from './Input';
import {
  GET_CURRENT_ORGANIZATION,
  UPDATE_ORGANIZATION_SELF_OPT_IN,
} from '../graphql/queries';
import Button from './Button';
import Loader from './Loader';
import Error from './Error';
import * as Price from '../util/Price';
import { getErrorMessage } from '../util/ErrorUtil';
// import * as phone from '../util/PhoneNumber';
import PDFTemplate from './PDFTemplate';
import '../assets/fonts/Poppins-SemiBold-normal';
import { RadioContainer, InputSection, Label } from './campaign/CampaignFormElements';
import { IconEnum } from './Icons';
import Icon from './Icon';
import HelpSidebar from './HelpSidebar';

const MAX_DESCRIPTION_CHARS = 1500;
const MAX_DESCRIPTION_CHARS_ERROR = `Description must be no longer than ${MAX_DESCRIPTION_CHARS} characters`;

const LoaderContainer = styled.div`
  position: absolute;
  top: calc(50vh - 60px);
  left: calc(50vw - 60px);
`;

const Form = styled.form`
  width: 65%;

  @media (max-width: 991px) {
    width: 100%;
  }
`;
const QrCodeContainer = styled.div`
  display: none;
`;

const CheckBoxSection = styled(InputSection)`
  @media screen and (max-width: 991px) {
    width: 100% !important;
    justify-content: ${(props) => props.justify || 'flex-start'};
    padding: 10px 0 0 0;
  }
  @media screen and (max-width: 480px) {
    flex-direction: column;
  }
`;

const TooltipStyles = createGlobalStyle`
.__react_component_tooltip{
      display: inline-block !important;
      max-width: 300px;
    }

    .css-1rhbuit-multiValue{
      font-size:13px !important;
      background: rgba(26,188,156,0.1) !important;
      &>div{
        color: red;
      };
    };

`;

export default function SelfOptIn() {
  const [keyword, setKeyword] = useState('');
  const [phrase, setPhrase] = useState('');

  const { data, loading } = useQuery(GET_CURRENT_ORGANIZATION);
  const defaultDescription = `Text up to $${Price.output(
    data?.getCurrentOrganization?.configurations?.firstTimeRecipientCredit,
    true,
  )} discounts at ${
    data?.getCurrentOrganization?.name
  } to your friends through Check This Out and save $${Price.output(
    data?.getCurrentOrganization?.configurations.affiliateCredit,
    true,
  )} on your next ${
    data?.getCurrentOrganization?.name
  } visit with each discount redeemed! By scanning the QR code or texting the keyword you opt-in to receive SMS messages from ${
    data?.getCurrentOrganization?.name
  } about special dishes, events, and other updates.`;
  const [description, setDescription] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [referralOptIn, setReferralOptIn] = useState(data?.getCurrentOrganization?.referralOptIn || false);
  const [enrollmentText, setSuccessEnrollmentText] = useState(data?.getCurrentOrganization?.enrollmentText);
  const [updateSelfOptIn, { loading: updateLoading }] = useMutation(
    UPDATE_ORGANIZATION_SELF_OPT_IN,
    {
      variables: {
        keyword,
        phrase,
        description,
        enrollmentText,
        referralOptIn,
      },
      onError(error) {
        setErrorMsg(getErrorMessage(error));
      },
      onCompleted() {
        setErrorMsg('');
      },
    },
  );

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarType, setSidebarType] = useState('');

  useEffect(() => {
    if (data && data.getCurrentOrganization) {
      const { getCurrentOrganization } = data;
      setKeyword(getCurrentOrganization.keyword);
      setPhrase(getCurrentOrganization.phrase);
      if (getCurrentOrganization.description) setDescription(getCurrentOrganization.description);
      else setDescription(defaultDescription);
    }
  }, [data]);

  const currentOrganisation = data?.getCurrentOrganization;

  const openSidebar = useCallback((type) => {
    if (!currentOrganisation) return;
    const { name } = currentOrganisation;

    setSidebarType({ contentType: type, orgName: name });
    setIsSidebarOpen(true);
  }, [currentOrganisation]);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const generateTemplate = useCallback(() => {
    if (!currentOrganisation) return;
    const {
      keyword: orgKeyword,
      phrase: orgPhrase,
      description: dynamicDescription,
    } = currentOrganisation;
    if (orgKeyword && orgPhrase) {
      const qrCodeSvg = document.getElementById('QRCode');
      const pngUrl = qrCodeSvg.toDataURL('image/png');
      const string = renderToStaticMarkup(
        <PDFTemplate
          QRCode={pngUrl}
          organization={currentOrganisation}
          description={dynamicDescription ?? description}
        />,
      );
      const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
      doc.addFont('Poppins-SemiBold-normal.ttf', 'Poppins', 'normal');
      doc.html(string, {
        callback(doc) {
          doc.save('CTO-OptIn-Template.pdf');
        },
        x: 150,
        y: 32,
        html2canvas: {
          scale: 0.8,
        },
      });
    } else {
      toast.error('Please add keyword and phrase to generate template.');
    }
  }, [currentOrganisation, description]);

  if (loading) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  if (!currentOrganisation) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  const contactTo = currentOrganisation.phoneNumber || currentOrganisation.campaignPhoneNumber;
  const linkText = `Click this link to text your friends up to a $${Price.output(currentOrganisation.configurations?.firstTimeRecipientCredit, true)} discount and you can earn $${Price.output(currentOrganisation.configurations?.affiliateCredit, true)} credit at ${currentOrganisation.name} for each discount that's redeemed ${window.location.origin}/user/invite/?orgId=${currentOrganisation._id}`;

  return (
    <>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          updateSelfOptIn();
        }}
      >
        <Card padding="20px 30px">
          <div data-help="self-opt-in-customize" style={{ width: '100%' }}>
            <Input
              type="text"
              width="100%"
              header="KEYWORD"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              required
              maxLength={15}
              infoButton={true}
              onMouseEnter={() => openSidebar('keyword')}
              onMouseLeave={() => closeSidebar()}
            />

            <Input
              type="text"
              header="SELF OPT-IN TEXT MESSAGE"
              width="100%"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              required
              infoButton={true}
              onMouseEnter={() => openSidebar('selfOtpIn')}
              onMouseLeave={() => closeSidebar()}
            />
            <TextAreaInput
              type="text"
              width="100%"
              value={description}
              onChange={(e) => {
                const { value } = e.target;
                if (value.length > MAX_DESCRIPTION_CHARS) {
                  setDescriptionError(MAX_DESCRIPTION_CHARS_ERROR);
                } else {
                  setDescriptionError('');
                  setDescription(e.target.value);
                }
              }}
              required
              header="DESCRIPTION ON SELF OPT-IN GRAPHIC"
              error={descriptionError}
              infoButton={true}
              onMouseEnter={() => openSidebar('optInGraphic')}
              onMouseLeave={() => closeSidebar()}
            />{' '}
            {errorMsg && (
              <Error
                error={errorMsg}
                margin="20px 0px 0px 0px"
                position="relative"
                textAlign="left"
              />
            )}
            <TextAreaInput
              type="text"
              width="100%"
              header="SUCCESSFUL ENROLLMENT TEXT"
              value={enrollmentText}
              required
              onChange={(e) => {
                setSuccessEnrollmentText(e.target.value);
              }}
              infoButton={true}
              onMouseEnter={() => openSidebar('enrollmentText')}
              onMouseLeave={() => closeSidebar()}
            />
            <CheckBoxSection noBorder>
              <RadioContainer>
                <span
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <Input
                    width="25px"
                    height="20px"
                    type="checkbox"
                    checked={referralOptIn}
                    onChange={() => setReferralOptIn(!referralOptIn)}
                  />
                  <Label>
                    Check here to include referral program opt-in text.
                  </Label>

                  <Icon
                    size={12}
                    margin="0 5px"
                    icon={IconEnum.infoRegular}
                    onMouseEnter={() => openSidebar('includeReferralOptInText')}
                    onMouseLeave={() => closeSidebar()}
                  />
                </span>

              </RadioContainer>
            </CheckBoxSection>
          </div>
          {referralOptIn && (
            <TextAreaInput
              type="text"
              width="100%"
              header=" PREVIEW SUCCESSFUL ENROLLMENT TEXT"
              value={`${enrollmentText}\n\n${linkText}`}
            />
          )}
          <TooltipStyles />
        </Card>
        <Button
          margin="40px 10px"
          text="UPDATE SELF OPT-IN"
          type="submit"
          width="fit-content"
          loading={updateLoading}
        />
        {contactTo && (
          <Button
            margin="40px 10px"
            text="GENERATE TEMPLATE"
            type="button"
            width="fit-content"
            onClick={generateTemplate}
          />
        )}
      </Form>
      {contactTo && (
        <QrCodeContainer>
          <QRCode
            value={`SMSTO:${contactTo}:${currentOrganisation.phrase}`}
            id="QRCode"
          />
        </QrCodeContainer>
      )}

      <HelpSidebar isOpen={isSidebarOpen} closeSidebar={closeSidebar} sidebarType={sidebarType} />
      <ReactTooltip
        place="right"
        type="dark"
        effect="solid"
        clickable={false}
        multiline={true}
      />
    </>
  );
}
